import React, { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { RootState } from "../../Store/store";
import { useGetOnboardingHomeStatesQuery } from "../../Services/api.user.slice";
import { DASH } from "../../Pages/PropertyDetailPage/dashboard/tokens";
import { isRejectedAcked } from "./onboardingStorage";

interface OnboardingHome {
  propertyId: string;
  address?: string | null;
  status: "APPROVED" | "REJECTED";
  apnId?: string | null;
  statusUpdatedAt?: string | null;
  link?: string | null;
}

// How often to check for a state change while browsing away from the home
// dashboard. Matches the notification bell's cadence.
const POLL_MS = 2 * 60 * 1000;
// Don't flood the screen if several homes need attention at once.
const MAX_PER_RUN = 2;

/**
 * Site-wide onboarding nudge. On every authenticated page it polls for homes
 * whose claim just flipped (approved and not yet toured, or rejected) and, when
 * the user is NOT on a property dashboard, shows a toast that routes them to
 * that home — where the page's own trigger opens the welcome tour / "needs more
 * info" pop-up. On a property page it stays silent, because the page handles its
 * own. Each home is nudged at most once per session.
 */
const OnboardingManager: React.FC = () => {
  const { isAuthenticated } = useSelector((state: RootState) => state?.auth) || ({} as any);
  const location = useLocation();
  const navigate = useNavigate();

  // The canonical property dashboard route is /{id}/property-id/{id}.
  const onPropertyPage = location.pathname.includes("/property-id/");

  const { data: homes } = useGetOnboardingHomeStatesQuery(undefined, {
    skip: !isAuthenticated,
    pollingInterval: POLL_MS,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });

  const toastedRef = useRef<Set<string>>(new Set());

  const showNavToast = (kind: "APPROVED" | "REJECTED", home: OnboardingHome) => {
    const name = home.address || "your home";
    const label =
      kind === "APPROVED" ? (
        <span>
          🎉 <b>{name}</b> is verified — <b>Take the tour</b>
        </span>
      ) : (
        <span>
          We need a bit more on <b>{name}</b> — <b>See details</b>
        </span>
      );
    toast(
      (t) => (
        <Box
          role="button"
          tabIndex={0}
          onClick={() => {
            toast.dismiss(t.id);
            if (home.link) navigate(home.link);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              toast.dismiss(t.id);
              if (home.link) navigate(home.link);
            }
          }}
          sx={{ cursor: "pointer", fontSize: 14, color: "#1d2b2b", outline: "none" }}
        >
          {label}
        </Box>
      ),
      {
        duration: 9000,
        icon: kind === "APPROVED" ? "✅" : "📄",
        style: { borderLeft: `4px solid ${kind === "APPROVED" ? DASH.green : DASH.amber}` },
      }
    );
  };

  useEffect(() => {
    if (!isAuthenticated) {
      toastedRef.current.clear(); // fresh start for the next account
      return;
    }
    if (onPropertyPage || !Array.isArray(homes)) return;

    let shown = 0;
    for (const home of homes as OnboardingHome[]) {
      if (shown >= MAX_PER_RUN) break;
      if (!home?.link) continue;

      if (home.status === "APPROVED") {
        const key = `approved:${home.propertyId}`;
        if (toastedRef.current.has(key)) continue;
        toastedRef.current.add(key);
        showNavToast("APPROVED", home);
        shown += 1;
      } else if (home.status === "REJECTED") {
        if (isRejectedAcked(home.apnId, home.statusUpdatedAt)) continue;
        const key = `rejected:${home.apnId}:${home.statusUpdatedAt}`;
        if (toastedRef.current.has(key)) continue;
        toastedRef.current.add(key);
        showNavToast("REJECTED", home);
        shown += 1;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homes, isAuthenticated, onPropertyPage]);

  return null;
};

export default OnboardingManager;
